import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { autoSchedule, mondayOf, weekDays, CREW_SIZE_DEFAULT } from './autoSchedule'
import { notifyWorker } from '../../lib/notifyWorker'

const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
const STATUS_ORDER = ['scheduled', 'day_off', 'called_off', 'no_call_no_show']
const STATUS_LABELS = {
  scheduled: 'SCHED',
  day_off: 'OFF',
  called_off: 'CALLED OFF',
  no_call_no_show: 'NCNS',
}
const STATUS_COLORS = {
  scheduled: { bg: 'rgba(74,140,106,0.18)', border: 'rgba(74,140,106,0.5)', fg: '#6fbf8e' },
  day_off: { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.1)', fg: 'rgba(255,255,255,0.4)' },
  called_off: { bg: 'rgba(201,162,74,0.15)', border: 'rgba(201,162,74,0.4)', fg: '#c9a24a' },
  no_call_no_show: { bg: 'rgba(224,92,92,0.15)', border: 'rgba(224,92,92,0.4)', fg: '#e05c5c' },
}

function fmtShort(dateStr) {
  const d = new Date(dateStr + 'T00:00:00')
  return (d.getMonth() + 1) + '/' + d.getDate()
}
function fmtRange(monday) {
  const d2 = new Date(monday)
  d2.setDate(d2.getDate() + 5)
  return fmtShort(monday.toISOString().slice(0,10)) + ' – ' + fmtShort(d2.toISOString().slice(0,10))
}

export default function WeeklyScheduleGrid() {
  const { profile, activeBusiness, isOwner } = useAuth()
  const canManage = ['owner', 'office_staff', 'project_manager'].includes(profile?.role)

  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()))
  const [workers, setWorkers] = useState([])
  const [jobs, setJobs] = useState([])
  const [schedule, setSchedule] = useState([]) // worker_schedule rows for this week
  const [dayOffs, setDayOffs] = useState([])
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [editing, setEditing] = useState(null) // { user_id, date }
  const [autoBusy, setAutoBusy] = useState(false)
  const [blockedDates, setBlockedDates] = useState([])
  const [workerAvailability, setWorkerAvailability] = useState([])
  const [pubBusy, setPubBusy] = useState(false)

  const days = useMemo(() => weekDays(weekStart), [weekStart])
  const weekStartStr = weekStart.toISOString().slice(0, 10)
  const weekEndStr = days[days.length - 1]

  const showMsg = (m) => { setMessage(m); setTimeout(() => setMessage(''), 4000) }

  const load = useCallback(async () => {
    setLoading(true)

    let workersQ = supabase.from('profiles')
      .select('id, full_name, role, seniority_rank, hire_date, max_days_per_week, preferred_days_off, season_start, season_end, availability_status, business_ids, phone, is_active')
      .neq('role', 'store_partner')
      .eq('is_active', true)
    const { data: workerRows } = await workersQ
    const activeWorkers = (workerRows || []).filter((w) => {
      if (w.availability_status === 'inactive') return false
      if (w.availability_status === 'off_season') return false
      // Business filter
      if (activeBusiness && activeBusiness !== 'all') {
        if (!w.business_ids || !w.business_ids.includes(activeBusiness)) return false
      }
      return true
    }).sort((a, b) => {
      // Group by role priority, then seniority
      const roleOrder = ['owner','project_manager','crew_leader','office_staff','laborer']
      const ra = roleOrder.indexOf(a.role); const rb = roleOrder.indexOf(b.role)
      if (ra !== rb) return ra - rb
      const sa = a.seniority_rank || 999; const sb = b.seniority_rank || 999
      if (sa !== sb) return sa - sb
      return (a.full_name || '').localeCompare(b.full_name || '')
    })
    setWorkers(activeWorkers)

    // Jobs active anywhere in the week
    let jobsQ = supabase.from('jobs')
      .select('id, title, client_name, site_address, business_id, start_date, end_date, status, crew_leader_id, crew_size_needed')
      .in('status', ['scheduled', 'active', 'in_progress'])
      .lte('start_date', weekEndStr)
    const { data: jobRows } = await jobsQ
    const weekJobs = (jobRows || []).filter((j) => {
      if (j.end_date && j.end_date < weekStartStr) return false
      if (activeBusiness && activeBusiness !== 'all' && j.business_id !== activeBusiness) return false
      return true
    })
    setJobs(weekJobs)

    // Existing worker_schedule rows
    const { data: schedRows } = await supabase.from('worker_schedule')
      .select('*')
      .gte('schedule_date', weekStartStr)
      .lte('schedule_date', weekEndStr)
    setSchedule(schedRows || [])

    // Approved day-offs for this week
    const { data: doffs } = await supabase.from('day_off_requests')
      .select('id, user_id, request_date, status')
      .eq('status', 'approved')
      .gte('request_date', weekStartStr)
      .lte('request_date', weekEndStr)
    setDayOffs(doffs || [])

    // Blocked dates
    var { data: bdRows } = await supabase.from('worker_blocked_dates').select('*')
      .eq('status', 'approved').gte('blocked_date', weekStartStr).lte('blocked_date', weekEndStr)
    setBlockedDates(bdRows || [])

    // Worker availability
    var { data: avRows } = await supabase.from('worker_availability').select('*')
    setWorkerAvailability(avRows || [])

    setLoading(false)
  }, [weekStartStr, weekEndStr, activeBusiness])

  useEffect(() => { load() }, [load])

  const scheduleMap = useMemo(() => {
    const m = new Map()
    for (const row of schedule) m.set(row.user_id + '|' + row.schedule_date, row)
    return m
  }, [schedule])

  const jobMap = useMemo(() => {
    const m = new Map()
    for (const j of jobs) m.set(j.id, j)
    return m
  }, [jobs])

  const getCell = (userId, date) => scheduleMap.get(userId + '|' + date)

  const cycleStatus = async (userId, date) => {
    if (!canManage) return
    const cur = getCell(userId, date)
    const curStatus = cur?.status || 'day_off'
    const nextIdx = (STATUS_ORDER.indexOf(curStatus) + 1) % STATUS_ORDER.length
    const nextStatus = STATUS_ORDER[nextIdx]
    await upsertCell(userId, date, { status: nextStatus })
    if (nextStatus !== curStatus) {
      notifyWorker({
        userId,
        type: 'schedule_change',
        title: 'Schedule updated',
        message: fmtShort(date) + ' — ' + STATUS_LABELS[nextStatus],
        link: '/schedule',
      })
    }
  }

  const upsertCell = async (userId, date, patch) => {
    const cur = getCell(userId, date)
    if (cur) {
      const { data } = await supabase.from('worker_schedule')
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('id', cur.id)
        .select()
        .single()
      if (data) setSchedule((prev) => prev.map((r) => r.id === data.id ? data : r))
    } else {
      const { data } = await supabase.from('worker_schedule')
        .insert({
          user_id: userId,
          schedule_date: date,
          status: patch.status || 'scheduled',
          job_id: patch.job_id ?? null,
          assigned_by: profile?.id || null,
        })
        .select()
        .single()
      if (data) setSchedule((prev) => [...prev, data])
    }
  }

  const assignJob = async (userId, date, jobId) => {
    await upsertCell(userId, date, { status: 'scheduled', job_id: jobId })
    setEditing(null)
  }

  const runAutoSchedule = async () => {
    if (!canManage) return
    if (!window.confirm('Auto-schedule this week? Existing rows will be kept; empty slots will be filled.')) return
    setAutoBusy(true)

    // Treat any non-empty existing row as locked
    const locked = new Map()
    for (const r of schedule) locked.set(r.user_id + '|' + r.schedule_date, r)

    const assignments = autoSchedule({
      weekStart: weekStartStr,
      days,
      jobs,
      workers,
      approvedDayOffs: dayOffs.map((d) => ({ user_id: d.user_id, request_date: d.request_date })),
      blockedDates: blockedDates,
      workerAvailability: workerAvailability,
      locked,
    })

    // Insert only non-locked assignments
    const inserts = []
    for (const a of assignments) {
      const key = a.user_id + '|' + a.schedule_date
      if (locked.has(key)) continue
      inserts.push({
        user_id: a.user_id,
        schedule_date: a.schedule_date,
        status: a.status,
        job_id: a.job_id,
        assigned_by: profile?.id || null,
      })
    }
    if (inserts.length > 0) {
      const { error } = await supabase.from('worker_schedule').insert(inserts)
      if (error) {
        showMsg('Auto-schedule error: ' + error.message)
        setAutoBusy(false)
        return
      }
    }

    await load()
    setAutoBusy(false)
    showMsg('Auto-schedule complete (' + inserts.length + ' new assignments).')
  }

  const publishWeek = async () => {
    if (!canManage) return
    if (!window.confirm('Publish this week\'s schedule? All scheduled workers will be notified.')) return
    setPubBusy(true)
    const scheduledWorkers = {}
    for (const row of schedule) {
      if (row.status === 'scheduled') {
        if (!scheduledWorkers[row.user_id]) scheduledWorkers[row.user_id] = []
        scheduledWorkers[row.user_id].push(row)
      }
    }
    const userIds = Object.keys(scheduledWorkers)
    await Promise.all(userIds.map(async (uid) => {
      const rows = scheduledWorkers[uid].sort((a, b) => a.schedule_date.localeCompare(b.schedule_date))
      const dayList = rows.map((r) => fmtShort(r.schedule_date)).join(', ')
      await notifyWorker({
        userId: uid,
        type: 'schedule_published',
        title: 'Week ' + fmtRange(weekStart) + ' posted',
        message: rows.length + ' day' + (rows.length === 1 ? '' : 's') + ': ' + dayList,
        link: '/schedule',
      })
    }))
    setPubBusy(false)
    showMsg('Published to ' + userIds.length + ' workers.')
  }

  const clearWeek = async () => {
    if (!canManage) return
    if (!window.confirm('Clear all assignments this week? This cannot be undone.')) return
    const ids = schedule.map((r) => r.id)
    if (ids.length > 0) {
      await supabase.from('worker_schedule').delete().in('id', ids)
    }
    await load()
    showMsg('Week cleared.')
  }

  const shiftWeek = (delta) => {
    const d = new Date(weekStart)
    d.setDate(d.getDate() + 7 * delta)
    setWeekStart(mondayOf(d))
  }

  // Per-day headcount bookkeeping for banner
  const dayStats = useMemo(() => {
    const stats = {}
    for (const d of days) {
      const active = jobs.filter((j) => {
        if (j.status && !['scheduled','active','in_progress'].includes(j.status)) return false
        if (j.start_date && d < j.start_date) return false
        if (j.end_date && d > j.end_date) return false
        return true
      })
      const needed = active.reduce((s, j) => {
        const cs = j.crew_size_needed && j.crew_size_needed > 0 ? j.crew_size_needed : (CREW_SIZE_DEFAULT[j.business_id] || 3)
        return s + cs
      }, 0)
      const filled = schedule.filter((r) => r.schedule_date === d && r.status === 'scheduled').length
      stats[d] = { needed, filled, short: filled < needed, jobs: active }
    }
    return stats
  }, [days, jobs, schedule])

  if (loading) return <div style={styles.empty}>Loading schedule...</div>

  return (
    <div style={styles.wrap}>
      {message && <div style={styles.message}>{message}</div>}

      <div style={styles.toolbar}>
        <div style={styles.weekNav}>
          <button onClick={() => shiftWeek(-1)} style={styles.navBtn}>←</button>
          <div style={styles.weekLabel}>{fmtRange(weekStart)}</div>
          <button onClick={() => shiftWeek(1)} style={styles.navBtn}>→</button>
          <button onClick={() => setWeekStart(mondayOf(new Date()))} style={styles.todayBtn}>THIS WEEK</button>
        </div>
        {canManage && (
          <div style={styles.actions}>
            <button onClick={runAutoSchedule} disabled={autoBusy} style={styles.primaryBtn}>
              {autoBusy ? 'SCHEDULING...' : 'AUTO SCHEDULE'}
            </button>
            <button onClick={publishWeek} disabled={pubBusy} style={styles.publishBtn}>
              {pubBusy ? 'PUBLISHING...' : 'PUBLISH'}
            </button>
            {isOwner && (
              <button onClick={clearWeek} style={styles.dangerBtn}>CLEAR WEEK</button>
            )}
          </div>
        )}
      </div>

      {/* Day banners: short-staff warnings */}
      <div style={styles.dayStrip}>
        <div style={styles.nameSlot}></div>
        {days.map((d, i) => {
          const s = dayStats[d]
          return (
            <div key={d} style={{ ...styles.dayHead, ...(s.short ? styles.dayHeadShort : {}) }}>
              <div style={styles.dayHeadLabel}>{DAY_LABELS[i]}</div>
              <div style={styles.dayHeadDate}>{fmtShort(d)}</div>
              <div style={styles.dayHeadNeed}>
                {s.filled}/{s.needed}
                {s.short && <span style={styles.dayShort}> SHORT</span>}
              </div>
            </div>
          )
        })}
      </div>

      {/* Worker rows */}
      <div style={styles.grid}>
        {workers.map((w) => {
          const weekAssignments = schedule.filter((r) => r.user_id === w.id && r.status === 'scheduled').length
          return (
            <div key={w.id} style={styles.row}>
              <div style={styles.name}>
                <div style={styles.nameText}>{w.full_name || 'Unnamed'}</div>
                <div style={styles.nameMeta}>
                  <span style={styles.roleTag}>{(w.role || '').replace('_',' ').toUpperCase()}</span>
                  {w.seniority_rank ? <span style={styles.rankTag}>#{w.seniority_rank}</span> : null}
                  <span style={styles.weekCount}>{weekAssignments}d</span>
                </div>
              </div>
              {days.map((d) => {
                const cell = getCell(w.id, d)
                const status = cell?.status || 'day_off'
                const col = STATUS_COLORS[status] || STATUS_COLORS.day_off
                const job = cell?.job_id ? jobMap.get(cell.job_id) : null
                const hasApprovedOff = dayOffs.some((x) => x.user_id === w.id && x.request_date === d)
                return (
                  <div
                    key={d}
                    style={{
                      ...styles.cell,
                      backgroundColor: col.bg,
                      borderColor: col.border,
                      color: col.fg,
                      cursor: canManage ? 'pointer' : 'default',
                    }}
                    onClick={() => canManage && cycleStatus(w.id, d)}
                    onContextMenu={(e) => { e.preventDefault(); if (canManage) setEditing({ user_id: w.id, date: d }) }}
                    title={canManage ? 'Click to cycle status, right-click to assign job' : ''}
                  >
                    <div style={styles.cellStatus}>{STATUS_LABELS[status]}</div>
                    {job && <div style={styles.cellJob}>{job.title || job.client_name}</div>}
                    {hasApprovedOff && <div style={styles.cellOffTag}>REQ OFF</div>}
                    {canManage && status === 'scheduled' && !job && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditing({ user_id: w.id, date: d }) }}
                        style={styles.assignBtn}
                      >ASSIGN JOB</button>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>

      {/* Job-assign modal */}
      {editing && (
        <JobAssignModal
          jobs={jobs.filter((j) => {
            if (j.start_date && editing.date < j.start_date) return false
            if (j.end_date && editing.date > j.end_date) return false
            return true
          })}
          currentJobId={getCell(editing.user_id, editing.date)?.job_id}
          onPick={(jobId) => assignJob(editing.user_id, editing.date, jobId)}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

function JobAssignModal({ jobs, currentJobId, onPick, onClose }) {
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalTitle}>ASSIGN TO JOB</div>
        <div style={styles.modalList}>
          <button onClick={() => onPick(null)} style={{ ...styles.jobBtn, ...(currentJobId == null ? styles.jobBtnActive : {}) }}>
            <div style={styles.jobBtnTitle}>— Floating / no specific job —</div>
          </button>
          {jobs.map((j) => (
            <button key={j.id} onClick={() => onPick(j.id)} style={{ ...styles.jobBtn, ...(j.id === currentJobId ? styles.jobBtnActive : {}) }}>
              <div style={styles.jobBtnTitle}>{j.title || j.client_name || 'Untitled'}</div>
              <div style={styles.jobBtnSub}>{j.site_address || ''} · {j.business_id === 'pools' ? 'CPP' : 'RBC'}</div>
            </button>
          ))}
          {jobs.length === 0 && <div style={styles.empty}>No jobs active this day.</div>}
        </div>
        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.cancelBtn}>CLOSE</button>
        </div>
      </div>
    </div>
  )
}

const styles = {
  wrap: { fontFamily: "'Barlow', sans-serif" },
  message: { backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '3px', padding: '12px 16px', color: '#4a8c6a', fontSize: '14px', marginBottom: '16px' },
  toolbar: { display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  weekNav: { display: 'flex', alignItems: 'center', gap: '8px' },
  navBtn: { width: '32px', height: '32px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '14px', cursor: 'pointer' },
  weekLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '15px', letterSpacing: '2px', color: '#b48c3c', minWidth: '140px', textAlign: 'center' },
  todayBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '6px 10px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1.5px', cursor: 'pointer' },
  actions: { display: 'flex', gap: '8px' },
  primaryBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '10px 16px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  publishBtn: { backgroundColor: '#4a8c6a', border: 'none', borderRadius: '3px', padding: '10px 16px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  dangerBtn: { backgroundColor: 'transparent', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '3px', padding: '10px 14px', color: '#e05c5c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1.5px', cursor: 'pointer' },
  dayStrip: { display: 'grid', gridTemplateColumns: '180px repeat(6, 1fr)', gap: '6px', marginBottom: '6px' },
  nameSlot: {},
  dayHead: { backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '8px 10px', textAlign: 'center' },
  dayHeadShort: { backgroundColor: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.3)' },
  dayHeadLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.6)' },
  dayHeadDate: { fontSize: '13px', color: '#ffffff', marginTop: '2px' },
  dayHeadNeed: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '1px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' },
  dayShort: { color: '#e05c5c', marginLeft: '4px' },
  grid: { display: 'flex', flexDirection: 'column', gap: '6px' },
  row: { display: 'grid', gridTemplateColumns: '180px repeat(6, 1fr)', gap: '6px' },
  name: { display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '6px 10px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px' },
  nameText: { fontSize: '13px', color: '#ffffff', fontWeight: 500 },
  nameMeta: { display: 'flex', gap: '4px', marginTop: '3px', alignItems: 'center', flexWrap: 'wrap' },
  roleTag: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '1px', color: 'rgba(255,255,255,0.35)' },
  rankTag: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', color: '#b48c3c', backgroundColor: 'rgba(180,140,60,0.1)', padding: '1px 5px', borderRadius: '2px' },
  weekCount: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginLeft: 'auto' },
  cell: { padding: '6px', border: '1px solid', borderRadius: '3px', minHeight: '54px', display: 'flex', flexDirection: 'column', justifyContent: 'center', fontSize: '11px', textAlign: 'center', transition: 'background 0.15s' },
  cellStatus: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '1px', fontWeight: 700 },
  cellJob: { fontSize: '10px', marginTop: '3px', color: 'rgba(255,255,255,0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cellOffTag: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', color: '#c9a24a', marginTop: '3px' },
  assignBtn: { marginTop: '4px', background: 'none', border: '1px dashed rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.45)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '1px', cursor: 'pointer', padding: '2px 4px', borderRadius: '2px' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#0d1420', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '6px', width: 'min(90vw, 500px)', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  modalTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  modalList: { padding: '12px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' },
  jobBtn: { backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '10px 12px', textAlign: 'left', cursor: 'pointer', color: '#ffffff' },
  jobBtnActive: { borderColor: '#b48c3c', backgroundColor: 'rgba(180,140,60,0.1)' },
  jobBtnTitle: { fontSize: '13px', color: '#ffffff' },
  jobBtnSub: { fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' },
  modalActions: { padding: '12px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'flex-end' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '8px 14px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', letterSpacing: '2px', cursor: 'pointer' },
  empty: { padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px' },
}
